/**
 * Base API Error Class
 *
 * Base error class that service-specific errors can extend.
 * Provides common properties and functionality for all API errors.
 *
 * @module services/shared/BaseApiError
 */

/**
 * Base API Error class with common properties
 */
export abstract class BaseApiError extends Error {
  /** HTTP status code */
  public readonly status?: number;
  /** Error message */
  public readonly message: string;
  /** Field-specific validation errors */
  public readonly fieldErrors?: Record<string, string>;
  /** Error code for programmatic handling */
  public readonly code?: string;

  constructor(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.code = code;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Check if this error has field-specific validation errors
   */
  hasFieldErrors(): boolean {
    return Boolean(this.fieldErrors && Object.keys(this.fieldErrors).length > 0);
  }

  /**
   * Get field error for a specific field
   */
  getFieldError(field: string): string | undefined {
    return this.fieldErrors?.[field];
  }

  /**
   * Convert error to JSON representation
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      code: this.code,
      fieldErrors: this.fieldErrors
    };
  }
}